/**
 * The generic CRUD-resource query-key factory (#2167, extracted from the
 * host's `lib/queryKeys.ts`).
 *
 * `ResourcePage` / `ResourceDetailPage` / the list controller / the form
 * controller all invalidate against these four shapes, so they travel with the
 * controllers rather than with the host's named per-entity registry. That
 * registry (`queryKeys.customers`, `queryKeys.audit`, …) stays in the host: it
 * enumerates THIS app's entities, which a package cannot know.
 *
 * The shapes are unchanged from the inline keys that predate the factory
 * (`[slug, 'list', params]`, `[slug, 'detail', id]`), so nothing splits a
 * cache.
 */

/**
 * Anything object-shaped (incl. typed interfaces like `ListParams`) or
 * `undefined`. Deliberately NOT `Record<string, unknown>`: a typed interface
 * without an index signature would be rejected even though it embeds fine as a
 * tuple element — a query key only ever compares the value structurally, never
 * enumerates its keys.
 */
export type KeyParams = object | undefined;

/**
 * Generic CRUD-resource key set, used by `ResourcePage` /
 * `ResourceDetailPage` for every entity that follows the same shape
 * (customers, products, orders, drivers, tasks, webhooks,
 * dispatch jobs/executions, …).
 *
 * Returning a plain factory keeps the keys readonly tuples (good for
 * TanStack Query inference) without forcing callers through a class.
 */
export function resourceKeys<S extends string>(slug: S) {
  return {
    all: () => [slug] as const,
    lists: () => [slug, 'list'] as const,
    list: (params: KeyParams) => [slug, 'list', params] as const,
    details: () => [slug, 'detail'] as const,
    detail: (id: string | number | undefined) => [slug, 'detail', id] as const,
  };
}
